'use client';

import React, { useState, useEffect, useCallback } from 'react';
import {
    idibuApi,
    type IdibuPortal,
    type SubscribeField,
} from '../services/idibu.api';
import PortalLogo from './PortalLogo';
import { IDIBU_READ_ONLY } from '../demo/demo-flag';

interface BoardSubscribeDrawerProps {
    portal: IdibuPortal;
    isSubscribed: boolean;
    onClose: () => void;
    onChanged: () => void;
}

export default function BoardSubscribeDrawer({
    portal,
    isSubscribed,
    onClose,
    onChanged,
}: BoardSubscribeDrawerProps) {
    const [fields, setFields] = useState<SubscribeField[]>([]);
    const [help, setHelp] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [fieldValues, setFieldValues] = useState<Record<string, string>>({});

    const loadInfo = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await idibuApi.getPortalSubscribeInfo(Number(portal.id));
            const sorted = [...(data.fields || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
            setFields(sorted);
            setHelp(data.help || null);
            const defaults: Record<string, string> = {};
            sorted.forEach(f => {
                defaults[f.name] = '';
            });
            setFieldValues(defaults);
        } catch (err: any) {
            setError(err?.response?.data?.error || err.message || 'Failed to load board details');
        } finally {
            setLoading(false);
        }
    }, [portal.id]);

    useEffect(() => {
        loadInfo();
    }, [loadInfo]);

    const handleSubscribe = async () => {
        const missingRequired = fields
            .filter(f => f.required === 'yes')
            .filter(f => !fieldValues[f.name]?.trim());

        if (missingRequired.length > 0) {
            setError(`Required fields missing: ${missingRequired.map(f => f.title).join(', ')}`);
            return;
        }

        setSubmitting(true);
        setError(null);
        setSuccess(null);
        try {
            const payload = Object.entries(fieldValues)
                .filter(([, v]) => v.trim())
                .map(([name, value]) => ({ name, value }));
            await idibuApi.subscribePortal(Number(portal.id), payload);
            setSuccess(`Successfully connected to ${portal.name}`);
            onChanged();
        } catch (err: any) {
            setError(err?.response?.data?.error || err.message || 'Failed to connect');
        } finally {
            setSubmitting(false);
        }
    };

    const handleUnsubscribe = async () => {
        if (!window.confirm(`Disconnect from ${portal.name}? You can reconnect later.`)) return;
        setSubmitting(true);
        setError(null);
        setSuccess(null);
        try {
            await idibuApi.unsubscribePortal(Number(portal.id));
            setSuccess(`Disconnected from ${portal.name}`);
            onChanged();
        } catch (err: any) {
            setError(err?.response?.data?.error || err.message || 'Failed to disconnect');
        } finally {
            setSubmitting(false);
        }
    };

    const updateField = (name: string, value: string) => {
        setFieldValues(prev => ({ ...prev, [name]: value }));
    };

    return (
        <>
            <div style={styles.overlay} onClick={onClose} />
            <div style={styles.drawer}>
                <div style={styles.drawerHeader}>
                    <button onClick={onClose} style={styles.closeBtn}>×</button>
                    <div style={styles.portalIdentity}>
                        <PortalLogo logo={portal.logo} name={portal.name} size={48} imageSize={36} />
                        <div>
                            <h3 style={styles.portalTitle}>{portal.name}</h3>
                            {portal.country && <span style={styles.portalCountry}>{portal.country}</span>}
                        </div>
                    </div>
                    {isSubscribed && <span style={styles.connectedBadge}>Connected</span>}
                </div>

                {portal.description && (
                    <p style={styles.description}>{portal.description}</p>
                )}

                <div style={styles.drawerBody}>
                    {loading && <div style={styles.loadingText}>Loading board details...</div>}

                    {error && (
                        <div style={styles.errorBanner}>
                            <span>{error}</span>
                            <button onClick={() => setError(null)} style={styles.dismissBtn}>×</button>
                        </div>
                    )}

                    {success && (
                        <div style={styles.successBanner}>{success}</div>
                    )}

                    {help && (
                        <div
                            style={styles.helpBlock}
                            dangerouslySetInnerHTML={{ __html: help }}
                        />
                    )}

                    {!loading && fields.length > 0 && (
                        <div style={styles.formSection}>
                            <h4 style={styles.formSectionTitle}>
                                {isSubscribed ? 'Update Credentials' : 'Board Credentials'}
                            </h4>
                            {fields.map(field => (
                                <div key={field.name} style={styles.formGroup}>
                                    <label style={styles.label}>
                                        {field.title}
                                        {field.required === 'yes' && <span style={styles.requiredStar}> *</span>}
                                        {field.type === 'login' && (
                                            <span style={styles.fieldTypeBadge}>Login</span>
                                        )}
                                    </label>
                                    {field.element === 'select' && field.values ? (
                                        <select
                                            value={fieldValues[field.name] || ''}
                                            onChange={e => updateField(field.name, e.target.value)}
                                            style={styles.select}
                                        >
                                            <option value="">-- Select --</option>
                                            {(Array.isArray(field.values) ? field.values : []).map((v, i) => (
                                                <option key={`${v.value}-${i}`} value={v.value}>{v.name}</option>
                                            ))}
                                        </select>
                                    ) : field.element === 'textarea' ? (
                                        <textarea
                                            value={fieldValues[field.name] || ''}
                                            onChange={e => updateField(field.name, e.target.value)}
                                            maxLength={field.maxchars || undefined}
                                            rows={3}
                                            style={styles.textarea}
                                        />
                                    ) : (
                                        <input
                                            type={field.type === 'login' ? 'password' : 'text'}
                                            value={fieldValues[field.name] || ''}
                                            onChange={e => updateField(field.name, e.target.value)}
                                            maxLength={field.maxchars || undefined}
                                            style={styles.input}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {!loading && fields.length === 0 && !error && (
                        <div style={styles.noFieldsMsg}>
                            No additional credentials required for this board.
                        </div>
                    )}
                </div>

                <div style={styles.drawerFooter}>
                    {isSubscribed ? (
                        <>
                            {fields.length > 0 && (
                                <button
                                    onClick={handleSubscribe}
                                    disabled={submitting || IDIBU_READ_ONLY}
                                    title={IDIBU_READ_ONLY ? 'Editing is disabled in demo mode' : undefined}
                                    style={styles.primaryBtn}
                                >
                                    {submitting ? 'Saving...' : 'Update Credentials'}
                                </button>
                            )}
                            <button
                                onClick={handleUnsubscribe}
                                disabled={submitting || IDIBU_READ_ONLY}
                                title={IDIBU_READ_ONLY ? 'Editing is disabled in demo mode' : undefined}
                                style={styles.dangerBtn}
                            >
                                {submitting ? 'Disconnecting...' : 'Disconnect Board'}
                            </button>
                        </>
                    ) : (
                        <button
                            onClick={handleSubscribe}
                            disabled={submitting || loading || IDIBU_READ_ONLY}
                            title={IDIBU_READ_ONLY ? 'Editing is disabled in demo mode' : undefined}
                            style={styles.primaryBtn}
                        >
                            {submitting ? 'Connecting...' : 'Connect Board'}
                        </button>
                    )}
                    <button onClick={onClose} style={styles.secondaryBtn}>Cancel</button>
                </div>
            </div>
        </>
    );
}

const styles: Record<string, React.CSSProperties> = {
    overlay: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.3)',
        zIndex: 999,
    },
    drawer: {
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: 460,
        maxWidth: '100vw',
        backgroundColor: '#fff',
        boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column' as const,
        overflow: 'hidden',
    },
    drawerHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '20px 24px',
        borderBottom: '1px solid #e5e7eb',
    },
    closeBtn: {
        background: 'none',
        border: 'none',
        fontSize: 24,
        cursor: 'pointer',
        color: '#9ca3af',
        padding: '0 4px',
        lineHeight: 1,
    },
    portalIdentity: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flex: 1,
    },
    logoWrap: {
        width: 40,
        height: 40,
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: '#f3f4f6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    logo: {
        maxWidth: 40,
        maxHeight: 40,
        objectFit: 'contain' as const,
    },
    portalTitle: {
        fontSize: 16,
        fontWeight: 600,
        margin: 0,
        color: '#111827',
    },
    portalCountry: {
        fontSize: 12,
        color: '#9ca3af',
    },
    connectedBadge: {
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 9999,
        backgroundColor: '#dcfce7',
        color: '#16a34a',
        whiteSpace: 'nowrap' as const,
    },
    description: {
        fontSize: 13,
        color: '#6b7280',
        padding: '12px 24px 0',
        margin: 0,
        lineHeight: 1.5,
    },
    drawerBody: {
        flex: 1,
        overflowY: 'auto' as const,
        padding: '16px 24px',
    },
    loadingText: {
        textAlign: 'center' as const,
        color: '#9ca3af',
        padding: 24,
        fontSize: 14,
    },
    helpBlock: {
        padding: '12px 16px',
        backgroundColor: '#f0f9ff',
        borderRadius: 8,
        fontSize: 13,
        color: '#1e40af',
        lineHeight: 1.5,
        marginBottom: 16,
        border: '1px solid #bfdbfe',
    },
    formSection: {
        marginBottom: 16,
    },
    formSectionTitle: {
        fontSize: 14,
        fontWeight: 600,
        color: '#374151',
        marginBottom: 12,
        marginTop: 0,
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 4,
        marginBottom: 14,
    },
    label: {
        fontSize: 13,
        fontWeight: 500,
        color: '#374151',
        display: 'flex',
        alignItems: 'center',
        gap: 4,
    },
    requiredStar: {
        color: '#dc2626',
    },
    fieldTypeBadge: {
        fontSize: 10,
        fontWeight: 500,
        padding: '1px 5px',
        borderRadius: 4,
        backgroundColor: '#fef3c7',
        color: '#92400e',
        marginLeft: 4,
    },
    input: {
        padding: '8px 12px',
        border: '1px solid #d1d5db',
        borderRadius: 8,
        fontSize: 14,
        outline: 'none',
    },
    textarea: {
        padding: '8px 12px',
        border: '1px solid #d1d5db',
        borderRadius: 8,
        fontSize: 14,
        outline: 'none',
        resize: 'vertical' as const,
        fontFamily: 'inherit',
    },
    select: {
        padding: '8px 12px',
        border: '1px solid #d1d5db',
        borderRadius: 8,
        fontSize: 14,
        backgroundColor: '#fff',
    },
    noFieldsMsg: {
        padding: 24,
        textAlign: 'center' as const,
        color: '#6b7280',
        fontSize: 14,
    },
    drawerFooter: {
        display: 'flex',
        gap: 10,
        padding: '16px 24px',
        borderTop: '1px solid #e5e7eb',
    },
    primaryBtn: {
        padding: '10px 20px',
        borderRadius: 8,
        border: 'none',
        backgroundColor: '#2563eb',
        color: '#fff',
        fontWeight: 600,
        fontSize: 14,
        cursor: 'pointer',
    },
    secondaryBtn: {
        padding: '10px 20px',
        borderRadius: 8,
        border: '1px solid #d1d5db',
        backgroundColor: '#fff',
        color: '#374151',
        fontWeight: 500,
        fontSize: 14,
        cursor: 'pointer',
    },
    dangerBtn: {
        padding: '10px 20px',
        borderRadius: 8,
        border: '1px solid #fca5a5',
        backgroundColor: '#fff',
        color: '#dc2626',
        fontWeight: 500,
        fontSize: 14,
        cursor: 'pointer',
    },
    errorBanner: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 14px',
        borderRadius: 8,
        backgroundColor: '#fef2f2',
        color: '#dc2626',
        fontSize: 13,
        marginBottom: 12,
        border: '1px solid #fecaca',
    },
    successBanner: {
        padding: '10px 14px',
        borderRadius: 8,
        backgroundColor: '#f0fdf4',
        color: '#16a34a',
        fontSize: 13,
        marginBottom: 12,
        border: '1px solid #bbf7d0',
    },
    dismissBtn: {
        background: 'none',
        border: 'none',
        color: '#dc2626',
        cursor: 'pointer',
        fontSize: 16,
        fontWeight: 700,
        padding: '0 4px',
    },
};
